/**
 * Decodes the in-app "visual editor" message HTML — the base64-of-compressed
 * blob the Gist SDK stores in `properties.gist.encodedMessageHtml`. Ember
 * inflates it with `pako.inflate`; this port uses the platform
 * `DecompressionStream` (no new dependency).
 */

type DecompressionFormat = 'gzip' | 'deflate' | 'deflate-raw';

const GZIP_MAGIC_BYTE_0 = 0x1f;
const GZIP_MAGIC_BYTE_1 = 0x8b;
const ZLIB_MAGIC_BYTE = 0x78;

/** Decode a base64 string to its raw bytes (`atob` throws on malformed input). */
function base64ToBytes(encoded: string): Uint8Array<ArrayBuffer> {
  const binary = atob(encoded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/**
 * Compression formats to try, ordered by the leading magic bytes: a gzip member
 * starts `1f 8b`, a zlib (deflate) stream starts with a `0x78` byte. When
 * neither matches we fall back to trying every format `pako.inflate` would.
 */
function candidateFormats(bytes: Uint8Array<ArrayBuffer>): DecompressionFormat[] {
  if (bytes[0] === GZIP_MAGIC_BYTE_0 && bytes[1] === GZIP_MAGIC_BYTE_1) {
    return ['gzip'];
  }
  if (bytes[0] === ZLIB_MAGIC_BYTE) {
    return ['deflate'];
  }
  return ['gzip', 'deflate', 'deflate-raw'];
}

/** A one-shot readable stream emitting `bytes` then closing — the input side of the pipe. */
function byteStream(bytes: Uint8Array<ArrayBuffer>): ReadableStream<Uint8Array<ArrayBuffer>> {
  return new ReadableStream({
    start(controller) {
      controller.enqueue(bytes);
      controller.close();
    },
  });
}

/** Inflate raw bytes through a `DecompressionStream` and decode the result as UTF-8. */
async function inflate(bytes: Uint8Array<ArrayBuffer>, format: DecompressionFormat): Promise<string> {
  const stream = byteStream(bytes).pipeThrough(new DecompressionStream(format));
  const buffer = await new Response(stream).arrayBuffer();
  return new TextDecoder('utf-8').decode(buffer);
}

/**
 * Decode a base64 string of gzip/zlib-compressed bytes back to its UTF-8 text.
 * Rejects when the input can't be decoded (bad base64 or no format inflates),
 * so callers can render an error state instead of crashing.
 */
export async function decodeGzipBase64(encoded: string): Promise<string> {
  const bytes = base64ToBytes(encoded);
  let lastError: unknown;
  for (const format of candidateFormats(bytes)) {
    try {
      return await inflate(bytes, format);
    } catch (error) {
      lastError = error;
    }
  }
  throw new Error('Unable to decode message HTML', { cause: lastError });
}
